import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import { BaseModel } from '@/api/base-model'
import { Credentials } from '@/api/model/credentials'
import { CompanyAPI } from '@/api/backend-client'
import { ReferralNode } from '@/api/model/referral-node'
import { Branding } from '@/api/model/branding'
import { AutoCloseSettings } from '@/api/model/auto-close-settings'
import { OrderSettings } from '@/api/model/order-settings'
import { GamificationSettings } from '@/api/model/gamification-settings'
import { CustomerPortalBranding } from '@/api/model/customer-portal-branding'
import {
    BranchType,
    CommentVisibility,
    CompanyTargetType,
    CompanyType,
    ContactView,
    CredentialsType,
    UserType,
    WidgetFlowType,
    WidgetGoal,
    WidgetPopupPosition,
    WidgetPopupType,
    WidgetType,
    WidgetPopupAnimation,
    WidgetFont,
    WidgetInformationTopic,
    WidgetPopupContent,
    WidgetAvatarStyle,
    Permission,
    CompanyLicenseType,
} from '@/api/types'
import { Source } from '@/api/model/source'

export class Company extends BaseModel<Company> {
    protected collectionName = CompanyAPI.collectionName
    private loadedIncomingReferrals: boolean = false
    private loadedOutgoingReferrals: boolean = false

    constructor(
        public id: string,
        public name: string,
        public type: CompanyType,
        public branding: Branding,
        public targetType: CompanyTargetType,
        public invoiceEmail: string,
        public defaultContactView: ContactView,
        public defaultCommentVisibility: CommentVisibility,
        public defaultDateRange: string|null,
        public potentialOrderValueRoles: string[],
        public powerBiCustomRoles: string[],
        public branches: Branch[],
        public incomingReferrals: object[],
        public outgoingReferrals: object[],
        public credentials: Credentials[],
        public users: User[],
        public sources: Source[],
        public pipelinesEnabled: boolean,
        public widgetEnabled: boolean,
        public widgetType: WidgetType,
        public widgetFlowTypes: WidgetFlowType[],
        public activatedWidgetFlowTypes: WidgetFlowType[],
        public widgetGoals: WidgetGoal[],
        public widgetPopupType: WidgetPopupType,
        public widgetPopupFlowType: WidgetFlowType,
        public widgetPopupPosition: WidgetPopupPosition,
        public widgetPopupAnimation: WidgetPopupAnimation,
        public widgetPopupBackgroundColor: string,
        public widgetPopupTextColor: string,
        public widgetPopupButtonColor: string,
        public widgetPopupButtonTextColor: string,
        public widgetPopupFont: WidgetFont,
        public widgetPopupContents: WidgetPopupContent[],
        public widgetAvatarBackgroundColor: string,
        public widgetAvatarTextColor: string,
        public widgetInformationTopics: WidgetInformationTopic[],
        public widgetChatTextBackground: string,
        public widgetChatText: string,
        public widgetChatUserText: string,
        public widgetChatUserTextBorder: string,
        public widgetChatInputBorder: string,
        public widgetChatInputText: string,
        public widgetChatInputButtonActive: string,
        public widgetChatInputButtonInactive: string,
        public widgetChatInputButton: string,
        public widgetChatInputButtonText: string,
        public widgetChatInputMultiText: string,
        public widgetChatInputMultiBorder: string,
        public widgetChatInputMultiSelectedText: string,
        public widgetChatInputMultiSelectedBorder: string,
        public widgetChatInputButtonActiveText: string,
        public widgetChatInputButtonInactiveText: string,
        public widgetAvatarCircle: string,
        public widgetAvatarStyle: WidgetAvatarStyle,
        public widgetPopupOpenTime: number,
        public winckCompanyId: string|null,
        public valuationPercentageModifierLow: number|null,
        public valuationPercentageModifierBase: number|null,
        public valuationPercentageModifierHigh: number|null,
        public autoCloseSettings: AutoCloseSettings,
        public orderSettings: OrderSettings,
        public gamificationSettings: GamificationSettings,
        public customerPortalBranding: CustomerPortalBranding,
        public permissions: Permission[],
        public license: boolean,
        public note: string|null,
        public pipelinePhaseShowAllTab: boolean,
        public betaOptInAvailable: boolean,
        public autoMailSettingsEnabled: boolean,
        public licenseType: CompanyLicenseType,
        public modules: string[],
        public amountOfUsers: number,
        public amountOfBranches: number,
        public virtualColleagueName: string|null,
        public virtualColleagueAvatar: string|null,
        public virtualColleagueAiEnabled: boolean,
        public createdAt: Date,
        public updatedAt: Date,
    ) { super(id) }

    hasCredentialsWithType(type: CredentialsType): boolean {
        return this.credentials
            .filter(credentials => credentials instanceof Credentials)
            .some((credentials: Credentials) => credentials.credentialsType === type)
    }

    getCredentialsWithType<T = Credentials>(type: CredentialsType): T[] {
        return this.credentials
            .filter(credentials => credentials instanceof Credentials)
            .filter((credentials: Credentials) => credentials.credentialsType === type) as unknown as T[]
    }

    getAvailableBranches(options: { type?: BranchType } = {}): Branch[] {
        return this.branches
            .filter(branch => branch.type === (options.type || BranchType.DEFAULT))
            .sort(((a, b) => a.name.localeCompare(b.name)))
    }

    getAvailableUsers(options: { branch?: Branch, type?: UserType } = {}) {
        let users = this.users
            .filter(user => user.active)
            .filter(user => user.type === (options.type || UserType.DEFAULT))

        if (options.branch) {
            users = users
                .filter(user => user.branches
                    .some(branch => branch.id === options.branch.id))
        }

        return users
            .sort(((a, b) => a.name.localeCompare(b.name)))
    }

    /**
     * @deprecated to be replaced by pipelines
     */
    async ensureIncomingReferralsAreLoaded(): Promise<void> {
        if (this.loadedIncomingReferrals === false) {
            this.loadedIncomingReferrals = true
            this.incomingReferrals = await CompanyAPI.getIncomingReferrals()
        }
    }

    /**
     * @deprecated to be replaced by pipelines
     */
    async ensureOutgoingReferralsAreLoaded(): Promise<void> {
        if (this.loadedOutgoingReferrals === false) {
            this.loadedOutgoingReferrals = true
            this.outgoingReferrals = await CompanyAPI.getOutgoingReferrals()
        }
    }

    /**
     * @deprecated to be replaced by pipelines
     */
    get handledReferralTypes(): string[] {
        this.ensureIncomingReferralsAreLoaded().then()
        this.ensureOutgoingReferralsAreLoaded().then()

        return [
            ...new Set([...this.incomingReferrals, ...this.outgoingReferrals]
                .filter((node: ReferralNode) => node.active)
                .map((node: ReferralNode) => node.type)),
        ]
    }

    /**
     * @deprecated to be replaced by pipelines
     */
    get handledInternalReferralTypes(): string[] {
        this.ensureIncomingReferralsAreLoaded().then()
        this.ensureOutgoingReferralsAreLoaded().then()

        return [
            ...new Set([...this.incomingReferrals || [], ...this.outgoingReferrals || []]
                .filter((node: ReferralNode) => node.active)
                .filter((node: ReferralNode) => node.source.id === node.target.id)
                .map((node: ReferralNode) => node.type)),
        ]
    }

    /**
     * @deprecated to be replaced by pipelines
     */
    get handledIncomingReferralTypes(): string[] {
        this.ensureIncomingReferralsAreLoaded().then()

        return [
            ...new Set([...this.incomingReferrals]
                .filter((node: ReferralNode) => node.active)
                .map((node: ReferralNode) => node.type)),
        ]
    }

    /**
     * @deprecated to be replaced by pipelines
     */
    get handledOutgoingReferralTypes(): string[] {
        this.ensureOutgoingReferralsAreLoaded().then()

        return [
            ...new Set([...this.outgoingReferrals]
                .filter((node: ReferralNode) => node.active)
                .map((node: ReferralNode) => node.type)),
        ]
    }

    get activeSources(): Source[] {
        return this.sources
            .filter(source => source.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    get emmaSource(): Source|null {
        const emmaSources = this.activeSources.filter(source => source.isEmmaSource)
        return emmaSources.length > 0 ? emmaSources[0] : null
    }

    get smartMarketingSource(): Source|null {
        const smartMarketingSources = this.activeSources.filter(source => source.isSmartMarketingSource)
        return smartMarketingSources.length > 0 ? smartMarketingSources[0] : null
    }

    static fromData(data: any): Company {
        if (data === null || typeof data === 'string' || data instanceof Company) {
            return data
        }

        const company = new Company(
            data.id,
            data.name,
            data.type,
            data.branding ? Branding.fromData(data.branding) : null,
            data.targetType,
            data.invoiceEmail,
            data.defaultContactView,
            data.defaultCommentVisibility,
            data.defaultDateRange,
            data.potentialOrderValueRoles,
            data.powerBiCustomRoles,
            Object.values(data.branches || {}).map(entry => Branch.fromData(entry)),
            Object.values(data.incomingReferrals || {}).map(entry => ReferralNode.fromData(entry)),
            Object.values(data.outgoingReferrals || {}).map(entry => ReferralNode.fromData(entry)),
            Object.values(data.credentials || {}).map(entry => Credentials.fromData(entry)),
            Object.values(data.users || {}).map(entry => User.fromData(entry)),
            Object.values(data.sources || {}).map(entry => Source.fromData(entry)),
            data.pipelinesEnabled,
            data.widgetEnabled,
            data.widgetType,
            data.widgetFlowTypes ?? [],
            data.activatedWidgetFlowTypes ?? [],
            data.widgetGoals ?? [],
            data.widgetPopupType,
            data.widgetPopupFlowType,
            data.widgetPopupPosition,
            data.widgetPopupAnimation,
            data.widgetPopupBackgroundColor,
            data.widgetPopupTextColor,
            data.widgetPopupButtonColor,
            data.widgetPopupButtonTextColor,
            data.widgetPopupFont,
            data.widgetPopupContents,
            data.widgetAvatarBackgroundColor,
            data.widgetAvatarTextColor,
            data.widgetInformationTopics,
            data.widgetChatTextBackground,
            data.widgetChatText,
            data.widgetChatUserText,
            data.widgetChatUserTextBorder,
            data.widgetChatInputBorder,
            data.widgetChatInputText,
            data.widgetChatInputButtonActive,
            data.widgetChatInputButtonInactive,
            data.widgetChatInputButton,
            data.widgetChatInputButtonText,
            data.widgetChatInputMultiText,
            data.widgetChatInputMultiBorder,
            data.widgetChatInputMultiSelectedText,
            data.widgetChatInputMultiSelectedBorder,
            data.widgetChatInputButtonActiveText,
            data.widgetChatInputButtonInactiveText,
            data.widgetAvatarCircle,
            data.widgetAvatarStyle,
            parseFloat(data.widgetPopupOpenTime),
            data.winckCompanyId,
            data.valuationPercentageModifierLow,
            data.valuationPercentageModifierBase,
            data.valuationPercentageModifierHigh,
            data.autoCloseSettings ? AutoCloseSettings.fromData(data.autoCloseSettings) : AutoCloseSettings.new(data.id),
            data.orderSettings ? OrderSettings.fromData(data.orderSettings) : OrderSettings.new(data.id),
            data.gamificationSettings ? GamificationSettings.fromData(data.gamificationSettings) : GamificationSettings.new(data.id),
            data.customerPortalBranding ? CustomerPortalBranding.fromData(data.customerPortalBranding) : null,
            data.permissions,
            data.license,
            data.note,
            data.pipelinePhaseShowAllTab,
            data.betaOptInAvailable,
            data.autoMailSettingsEnabled,
            data.licenseType,
            data.modules,
            data.amountOfUsers,
            data.amountOfBranches,
            data.virtualColleagueName,
            data.virtualColleagueAvatar,
            data.virtualColleagueAiEnabled,
            data.createdAt,
            data.updatedAt,
        )

        // widget still sends incoming/outgoing in company call, this prevents extra fetching
        // @todo: would be better to check if we have a login token set
        company.loadedIncomingReferrals = (company.incomingReferrals.length > 0)
        company.loadedOutgoingReferrals = (company.outgoingReferrals.length > 0)

        company.branches.forEach((branch: Branch) => branch.company = branch.company || company)
        company.users.forEach((user: User) => user.company = user.company || company)

        return company
    }
}
